<template>
	<loading-el v-if="loading"></loading-el>
	<h2 class="popup-headline">{{ t('popup.tableOfContent.title') }}</h2>
	<div class="popup-description">{{ t('popup.tableOfContent.desc') }}</div>
	<alert-message
		v-if="alert.isShown"
		:alert="alert"
		@close-alert="alertHide"
		class="popup-alert"
	></alert-message>
	<template v-if="settings">
		<uni-panel
			:panel-key="1"
			v-model:panel-opened-key="panelOpenedKey"
			:disabled="!settings.content.individual"
			class="mb--66"
		>
			<template #header-content>
				<div class="flex width--full flex--align-center">
					<div class="max-width--190 flex--grow">
						<span class="bold fs--20 color--black">{{ t('popup.tableOfContent.title') }}</span>
					</div>
					<div class="max-width--180 flex--grow">
						<field-toggle
							v-model:checked="settings.content.show"
							:label="t('popup.tableOfContent.label.show')"
						></field-toggle>
					</div>
					<div class="width--max-content">
						<field-toggle
							v-model:checked="settings.content.individual"
							:label="t('popup.tableOfContent.label.individual')"
						></field-toggle>
					</div>
				</div>
			</template>
			<template #default>
				<p class="typo-item fs--15 color--black lh--20 mb--20">
					{{ t('popup.tableOfContent.articles.desc1') }}
					<span class="bold">{{ t('popup.tableOfContent.articles.desc2') }}</span>
					{{ t('popup.tableOfContent.articles.desc3') }}
				</p>
				<div class="flex">
					<div class="table-content table-content--sort">
						<header class="table-content__header">{{ t('popup.tableOfContent.articles.individual') }}</header>
						<content-table
							class="scrollbar-custom"
							:class-id="'--sort'"
							:list="articlesOrdered"
							:depth="0"
							:emit-event="true"
							@drag-end="parseNestedArticles"
						/>
					</div>
					<div class="table-content">
						<header class="table-content__header">{{ t('popup.tableOfContent.articles.unsorted') }}</header>
						<content-table
							class="scrollbar-custom"
							:list="articles"
							:depth-max="0"
						/>
					</div>
				</div>
			</template>
		</uni-panel>
		<div class="popup-description">{{ t('popup.tableOfContent.subTitle') }}</div>
		<div class="flex width--full box-border flex--align-center p--5 border--color-black border--width-2 border--b-solid mb--15">
			<div class="max-width--190 flex--grow">
				<span class="fs--15 color--black bold">{{ t('dashboardOptions.glossary') }}</span>
			</div>
			<div class="flex--grow">
				<field-toggle
					v-model:checked="settings.glossary.show"
					:label="t('popup.tableOfContent.label.show')"
				></field-toggle>
			</div>
			<i
				@click="settingsOpen(settings.glossary.link)"
				class="fas fa-cogs cursor--pointer fs--20 color--rosy-pink"
			></i>
		</div>
		<div class="flex width--full box-border flex--align-center p--5 border--color-black border--width-2 border--b-solid mb--60">
			<div class="max-width--190 flex--grow">
				<span class="fs--15 color--black bold">{{ t('popup.translationsOverview.labels.sources.title')}}</span>
			</div>
			<div class="flex--grow">
				<field-toggle
					v-model:checked="settings.sources.show"
					:label="t('popup.tableOfContent.label.show')"
				></field-toggle>
			</div>
			<i
				@click="settingsOpen(settings.sources.link)"
				class="fas fa-cogs cursor--pointer fs--20 color--rosy-pink"
			></i>
		</div>
		<div class="button-wrapper mt--60">
			<div
				:class="isSaveAvailableClassname"
				@click="settingsSave"
				class="button button--popup bg--dusky-rose"
			>
				<i class="fas fa-save"></i> <span>{{ t('buttons.saveSettings') }}</span>
			</div>
		</div>
	</template>
</template>

<script setup lang="ts">
import LoadingEl from '@/components/ui/loading/LoadingEl.vue';
import AlertMessage from '@/components/ui/alert/AlertMessage.vue';
import UniPanel from '@/components/ui/panel/UniPanel.vue';
import FieldToggle from '@/components/fields/FieldToggle.vue';
import ContentTable from '@/components/ui/draggable/ContentTable.vue';
import { useTableOfContentsSettings } from '@/modules/table-of-contents';

const {
	t,
	loading,
	alert,
	alertHide,
	panelOpenedKey,
	settings,
	settingsOpen,
	isSaveAvailableClassname,
	settingsSave,
	articles,
	articlesOrdered,
	parseNestedArticles,
} = useTableOfContentsSettings();
</script>

<style lang="scss">
@use "@/scss/ui/table-content/table-content";
</style>
